import React, { useContext, useEffect, useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { Box, Checkbox, CircleLoader, Flex, SelectOption, Tooltip } from '@driftt/dds'
import { Select } from '@driftt/tide-selects'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import { fetchTeams } from 'api/CustomerAPI'
import ExpandableList from 'components/ExpandableList'
import { CurrentUserContext } from 'context/currentUserContext'
import { useAppSelector } from 'hooks/useRedux'
import { captureExceptionWithMessage } from 'utils/logRocket'
import { segmentTrack, TrackType } from 'utils/tracking'

import '../FiltersForm/styles.module.css'

export interface AdminFilterSettingsProps {
  sharedWithAll: boolean
  setSharedWithAll: (value: boolean) => void
  isDefault: boolean
  setIsDefault: (value: boolean) => void
  setAssignedTeams: (value: number[]) => void
  setAssignedUsers: (value: number[]) => void
  hasEmailNotifications: boolean
  setHasEmailNotifications: (value: boolean) => void
  hasDesktopNotifications: boolean
  setHasDesktopNotifications: (value: boolean) => void
  savedFilter?: DeserializedSavedFilter
  assignedTeams: number[]
  assignedUsers: number[]
}

const buildSelectOptions = (selectOptions: any, orgUsers: any) => {
  const teamOptions = selectOptions
    .map((team: any) => {
      return { label: team.name, value: team.id }
    })
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  const userOptions = orgUsers
    .map((user: any) => {
      return {
        label: user?.name || user?.email,
        value: user.id
      }
    })
    .sort((a: any, b: any) => a.label.localeCompare(b.label))

  return [
    { label: 'Teams', options: teamOptions },
    { label: 'Users', options: userOptions }
  ]
}

const selectStyles = {
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 40
  })
}

const AdminFilterSettings = ({
  sharedWithAll,
  setSharedWithAll,
  isDefault,
  setIsDefault,
  setAssignedTeams,
  setAssignedUsers,
  hasDesktopNotifications,
  setHasDesktopNotifications,
  hasEmailNotifications,
  setHasEmailNotifications,
  savedFilter,
  assignedTeams,
  assignedUsers
}: AdminFilterSettingsProps) => {
  const [selectOptions, setSelectOptions] = useState<any>([])
  const [selectedOptions, setSelectedOptions] = useState<any>([])
  const [isAdminDefault, setIsAdminDefault] = useState(false)
  const { data, error } = useQuery({queryKey: ['fetchTeams'], queryFn: () => fetchTeams()})
  const { users } = useAppSelector(({ orgUsersById }) => orgUsersById)
  const currentUser = useContext(CurrentUserContext)

  useEffect(() => {
    segmentTrack(TrackType.ADMIN_MANAGED_FILTER_SETTINGS_VIEWED, {
      orgId: currentUser?.orgId
    })
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data && selectOptions.length === 0 && users) {
      const activeOrgUsers = Object.values(users).filter(
        (user: any) => (user.status === 'ENABLED' && user.name) || user.email
      )

      setSelectOptions(buildSelectOptions(data, activeOrgUsers))
    }
  }, [data, selectOptions, users])

  // Map team ids for easy lookup
  const TEAMS_MAP = useMemo(() => {
    if (!data) {
      return []
    }
    return data.reduce((acc: Record<number, string>, team: any) => {
      acc[team.id] = team.name
      return acc
    }, {})
  }, [data])
  // Map user ids for easy lookup
  const USERS_MAP = useMemo(() => {
    if (!users) {
      return []
    }

    return Object.values(users).reduce((acc: Record<number, string>, user: any) => {
      acc[user.id] = user.name || user.email
      return acc
    }, {})
  }, [users])

  // If we have a saved filter, set the select options to the saved filter's org settings
  useEffect(() => {
    if (savedFilter && TEAMS_MAP && selectOptions.length && USERS_MAP) {
      const { orgSettings } = savedFilter
      if (orgSettings) {
        const {
          usersWithFilter,
          teamsWithFilter,
          digestNotificationsTurnedOn,
          pushNotificationsTurnedOn
        } = orgSettings
        if (usersWithFilter && teamsWithFilter) {
          const options = teamsWithFilter
            .map((teamId: number) => {
              return { label: TEAMS_MAP[teamId], value: teamId }
            })
            .concat(
              usersWithFilter.map((userId: any) => {
                const label = USERS_MAP[userId]
                return {
                  label,
                  value: userId
                }
              })
            )

          setIsAdminDefault(true)
          setHasDesktopNotifications(pushNotificationsTurnedOn)
          setHasEmailNotifications(digestNotificationsTurnedOn)
          setSelectedOptions(options)
          setAssignedTeams(teamsWithFilter)
          setAssignedUsers(usersWithFilter)
        }
      }
    }
  }, [
    setAssignedTeams,
    setAssignedUsers,
    savedFilter,
    TEAMS_MAP,
    selectOptions,
    users,
    USERS_MAP,
    setHasDesktopNotifications,
    setHasEmailNotifications
  ])

  // If the user changes the default option to false, clear the assigned teams and users
  useEffect(() => {
    if (!isAdminDefault) {
      setAssignedTeams([])
      setAssignedUsers([])
    }
  }, [isAdminDefault, setAssignedTeams, setAssignedUsers])

  const shareWithAllIsDisable = !sharedWithAll

  useEffect(() => {
    if (shareWithAllIsDisable) {
      setIsAdminDefault(false)
    }
  }, [shareWithAllIsDisable])

  const bodyEl = document.querySelector('body')
  // Log errors to LogRocket
  if (error) {
    captureExceptionWithMessage('Error fetching select options in AdminFilterSettings', error)
  }
  return (
    <ExpandableList>
      <ExpandableList.Item title="My settings">
        <Flex css={{ paddingBottom: '$1' }} direction="column">
          <Checkbox label="Set as my default filter" checked={isDefault} onChange={setIsDefault} />
        </Flex>
      </ExpandableList.Item>
      <ExpandableList.Item openByDefault title="Org settings">
        <Flex css={{ paddingBottom: '$1' }} direction="column">
          <Checkbox
            label="Share saved filters with everyone in my organization"
            checked={sharedWithAll || isAdminDefault}
            onChange={setSharedWithAll}
          />
          <Tooltip
            content={
              shareWithAllIsDisable
                ? 'To set the default filter for a team or teammate, select share the filter with everyone in the organization'
                : ''
            }>
            <Checkbox
              label="Set as the default filter for the following teams and teammates:"
              checked={isAdminDefault}
              onChange={setIsAdminDefault}
              disabled={shareWithAllIsDisable}
            />
          </Tooltip>
          {isAdminDefault && (
            <Flex
              css={{
                paddingLeft: '$3'
              }}
              fullWidth
              direction="column">
              {selectOptions.length ? (
                <>
                  <Flex fullWidth>
                    <Box css={{ display: 'block' }} fullWidth>
                      <Select
                        className="filterSelect"
                        isMulti
                        clearable={false}
                        size={'small'}
                        selectStyles={selectStyles}
                        value={selectedOptions}
                        onChange={(val: any) => {
                          const { appliedTeams, appliedUsers } = val.reduce(
                            (
                              acc: {
                                appliedTeams: number[]
                                appliedUsers: number[]
                              },
                              value: SelectOption
                            ) => {
                              if (
                                // Ensure we only send numbers to the backend
                                !isNaN(+value.value) &&
                                TEAMS_MAP[value.value] === value.label &&
                                !acc.appliedTeams.includes(+value.value)
                              ) {
                                acc.appliedTeams.push(+value.value)
                                // Only send newly assigned team events to segment
                                if (!assignedTeams.includes(+value.value)) {
                                  segmentTrack(
                                    TrackType.ADMIN_MANAGED_FILTER_SETTINGS_USER_ASSIGNED,
                                    {
                                      orgId: currentUser?.orgId
                                    }
                                  )
                                }
                              } else if (
                                !isNaN(+value.value) &&
                                USERS_MAP[+value.value] === value.label &&
                                !acc.appliedUsers.includes(+value.value)
                              ) {
                                acc.appliedUsers.push(+value.value)
                                // Only send newly assigned user events to segment
                                if (!assignedUsers.includes(+value.value)) {
                                  segmentTrack(
                                    TrackType.ADMIN_MANAGED_FILTER_SETTINGS_USER_ASSIGNED,
                                    {
                                      orgId: currentUser?.orgId
                                    }
                                  )
                                }
                              }

                              return acc
                            },
                            { appliedTeams: [], appliedUsers: [] }
                          )

                          setAssignedTeams(appliedTeams)
                          setAssignedUsers(appliedUsers)
                          setSelectedOptions(val)
                        }}
                        options={selectOptions}
                        menuPortalTarget={bodyEl}
                        menuPlacement="auto"
                        minMenuHeight={245}
                        maxMenuHeight={245}
                        required
                      />
                    </Box>
                  </Flex>

                  <Checkbox
                    label=" Turn on daily digest email notifications"
                    checked={hasEmailNotifications}
                    onChange={setHasEmailNotifications}
                  />
                  <Checkbox
                    label="Turn on real time desktop notifications"
                    checked={hasDesktopNotifications}
                    onChange={setHasDesktopNotifications}
                  />
                </>
              ) : (
                <CircleLoader />
              )}
            </Flex>
          )}
        </Flex>
      </ExpandableList.Item>
    </ExpandableList>
  )
}

export default AdminFilterSettings
